import { useEffect, useState } from "react";
import {
  forgetAuthToken,
  loadAccountSessions,
  terminateAccountSession,
  terminateAllAccountSessions,
  type AccountSession,
} from "../lib/accountApi";

type SessionManagementProps = {
  apiBase: string;
  onSignedOut: () => void;
};

function sessionIdentity(userAgent: string | null) {
  const value = userAgent || "";
  let device = "Dispositivo não identificado";
  let kind: "phone" | "tablet" | "computer" = "computer";
  if (/iPhone/i.test(value)) { device = "iPhone"; kind = "phone"; }
  else if (/iPad/i.test(value)) { device = "iPad"; kind = "tablet"; }
  else if (/Android/i.test(value)) { device = /Mobile/i.test(value) ? "Telemóvel Android" : "Tablet Android"; kind = /Mobile/i.test(value) ? "phone" : "tablet"; }
  else if (/Windows/i.test(value)) device = "Computador Windows";
  else if (/Macintosh|Mac OS X/i.test(value)) device = "Mac";
  else if (/Linux/i.test(value)) device = "Computador Linux";

  let browser = "Browser não identificado";
  if (/EdgA?\/|EdgiOS\//i.test(value)) browser = "Microsoft Edge";
  else if (/OPR\/|Opera/i.test(value)) browser = "Opera";
  else if (/CriOS\/|Chrome\//i.test(value)) browser = "Google Chrome";
  else if (/FxiOS\/|Firefox\//i.test(value)) browser = "Mozilla Firefox";
  else if (/Safari\//i.test(value)) browser = "Safari";
  return { device, browser, kind };
}

function relativeActivity(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "atividade desconhecida";
  const elapsed = Math.max(0, Date.now() - date.getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `há ${days} ${days === 1 ? "dia" : "dias"}`;
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

function fullDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Data desconhecida" : date.toLocaleString("pt-PT", { dateStyle: "medium", timeStyle: "short" });
}

function DeviceIcon({ kind }: { kind: "phone" | "tablet" | "computer" }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {kind === "computer"
      ? <><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/></>
      : <><rect x={kind === "tablet" ? "5" : "7"} y="2" width={kind === "tablet" ? "14" : "10"} height="20" rx="2"/><path d="M10 18h4"/></>}
  </svg>;
}

export function SessionManagement({ apiBase, onSignedOut }: SessionManagementProps) {
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try { setSessions(await loadAccountSessions(apiBase)); }
    catch (reason) {
      if (reason instanceof Error && reason.message === "UNAUTHENTICATED") {
        forgetAuthToken();
        onSignedOut();
        return;
      }
      setError("Não foi possível carregar as sessões. Tente novamente.");
    } finally { setLoading(false); }
  };

  useEffect(() => { void refresh(); }, []);

  const terminateOne = async (session: AccountSession) => {
    const prompt = session.current
      ? "Ao terminar esta sessão terá de iniciar sessão novamente neste dispositivo. Continuar?"
      : "Terminar a sessão selecionada?";
    if (!window.confirm(prompt)) return;
    setBusyId(session.id);
    setError("");
    try {
      const result = await terminateAccountSession(apiBase, session.id);
      if (result.currentTerminated) {
        forgetAuthToken();
        onSignedOut();
        return;
      }
      setSessions((current) => current.filter((item) => item.id !== session.id));
    } catch { setError("Não foi possível terminar essa sessão. Tente novamente."); }
    finally { setBusyId(""); }
  };

  const terminateAll = async () => {
    if (!window.confirm("Terminar todas as sessões, incluindo a deste dispositivo? Terá de iniciar sessão novamente.")) return;
    setBusyId("all");
    setError("");
    try {
      await terminateAllAccountSessions(apiBase);
      forgetAuthToken();
      onSignedOut();
    } catch {
      setError("Não foi possível terminar todas as sessões. Tente novamente.");
      setBusyId("");
    }
  };

  if (loading) return <div className="session-loading" role="status"><span/><p>A carregar sessões…</p></div>;
  if (error && !sessions.length) return <div className="session-error" role="alert"><p>{error}</p><button type="button" onClick={() => void refresh()}>Tentar novamente</button></div>;

  return <div className="session-management">
    <p className="session-intro">Estes são os dispositivos e browsers onde a sua conta Precision tem sessão iniciada.</p>
    {error && <p className="session-inline-error" role="alert">{error}</p>}
    <div className="session-cards">
      {sessions.map((session) => {
        const identity = sessionIdentity(session.userAgent);
        return <article className={`session-card${session.current ? " current" : ""}`} key={session.id}>
          <div className="session-device-icon"><DeviceIcon kind={identity.kind}/></div>
          <div className="session-card-copy">
            <div className="session-card-title"><h3>{identity.device}</h3>{session.current && <span>Este dispositivo</span>}</div>
            <p>{identity.browser}</p>
            <dl>
              <div><dt>Última atividade</dt><dd>{relativeActivity(session.lastSeenAt)}</dd></div>
              <div><dt>Iniciada</dt><dd>{fullDate(session.createdAt)}</dd></div>
            </dl>
            <button type="button" className="session-terminate" disabled={Boolean(busyId)} onClick={() => void terminateOne(session)}>{busyId === session.id ? "A terminar…" : "Terminar sessão"}</button>
          </div>
        </article>;
      })}
    </div>
    {sessions.length > 0 && <button type="button" className="session-terminate-all" disabled={Boolean(busyId)} onClick={() => void terminateAll()}>{busyId === "all" ? "A terminar sessões…" : "Terminar todas as sessões"}</button>}
    <p className="session-security-note">Se não reconhecer um dispositivo, termine essa sessão. A alteração da palavra-passe e a autenticação em dois passos serão adicionadas na próxima fase.</p>
  </div>;
}
